// Club services configuration — defines all revenue-generating services

export interface TimeSlot {
  id: string
  label: string
  priceMultiplier: number
  hours: number // how many hours this slot covers
}

export interface ServiceRequirement {
  type: 'building' | 'staff' | 'courts' | 'reputation'
  value: string | number
}

export interface ServiceDefinition {
  id: string
  name: string
  emoji: string
  description: string
  category: 'court' | 'class' | 'event' | 'retail'
  basePrice: number
  timeSlots?: TimeSlot[] // for court-rental type services
  maxPerDay?: number // max events/sessions per day
  requirements: ServiceRequirement[]
  // Demand sensitivity factors (higher = more impact)
  reputationWeight: number // how much reputation affects demand
  priceWeight: number // how much price deviation affects demand
}

export const TIME_SLOTS: TimeSlot[] = [
  { id: 'morning', label: 'Manana (8-13h)', priceMultiplier: 0.7, hours: 5 },
  { id: 'afternoon', label: 'Tarde (13-18h)', priceMultiplier: 1.0, hours: 5 },
  { id: 'evening', label: 'Noche (18-22h)', priceMultiplier: 1.5, hours: 4 },
]

export const SERVICES: ServiceDefinition[] = [
  {
    id: 'court_rental',
    name: 'Alquiler de Pistas',
    emoji: '🎾',
    description: 'Alquiler por horas con franjas diferenciadas',
    category: 'court',
    basePrice: 20, // per hour per court
    timeSlots: TIME_SLOTS,
    requirements: [
      { type: 'courts', value: 1 },
    ],
    reputationWeight: 0.15,
    priceWeight: 0.02,
  },
  {
    id: 'group_classes',
    name: 'Clases Grupales',
    emoji: '🎓',
    description: 'Clases para grupos de hasta 4 alumnos',
    category: 'class',
    basePrice: 25, // per session
    maxPerDay: 3,
    requirements: [
      { type: 'staff', value: 'coach' },
      { type: 'courts', value: 1 },
    ],
    reputationWeight: 0.1,
    priceWeight: 0.01,
  },
  {
    id: 'kids_camp',
    name: 'Campus Infantil',
    emoji: '👶',
    description: 'Programa de padel para ninos (mananas)',
    category: 'class',
    basePrice: 15, // per kid per session
    maxPerDay: 1, // morning only
    requirements: [
      { type: 'staff', value: 'coach' },
      { type: 'courts', value: 2 },
    ],
    reputationWeight: 0.08,
    priceWeight: 0.015,
  },
  {
    id: 'private_events',
    name: 'Eventos Privados',
    emoji: '🎉',
    description: 'Cumpleanos, team building, corporativos',
    category: 'event',
    basePrice: 500, // flat fee per event
    maxPerDay: 1,
    requirements: [
      { type: 'courts', value: 2 },
      { type: 'building', value: 'reception' },
      { type: 'reputation', value: 3 },
    ],
    reputationWeight: 0.2,
    priceWeight: 0.005,
  },
  {
    id: 'pro_shop',
    name: 'Pro Shop',
    emoji: '🏪',
    description: 'Venta de material deportivo',
    category: 'retail',
    basePrice: 80, // base daily income
    requirements: [
      // The Pro Shop building's BuildingType is 'shop' (see buildingConfig.ts);
      // it was 'pro_shop' here, which never matched so the service never unlocked.
      { type: 'building', value: 'shop' },
    ],
    reputationWeight: 0.1,
    priceWeight: 0,
  },
  {
    id: 'cafeteria',
    name: 'Cafeteria',
    emoji: '☕',
    description: 'Bebidas y snacks para jugadores',
    category: 'retail',
    basePrice: 60, // base daily income
    requirements: [
      { type: 'building', value: 'cafe' },
    ],
    reputationWeight: 0.08,
    priceWeight: 0,
  },
]

/** Market average price for a service (used to calculate price sensitivity) */
export function getMarketPrice(serviceId: string): number {
  const service = SERVICES.find(s => s.id === serviceId)
  return service?.basePrice ?? 20
}
